/**
 * Generic convertion utilities
 *	@module ods/convert
 */

//
//	Required
//

import {tr, getDecimalSeparator, getThousandsSeparator} from '@alias_root/src/framework/ods/ods';
import {HoursFormat} from '@alias_root/src/data/hourformatEnum';
import {isNumber} from '@alias_root/src/framework/ods/types';
import {roundByBase, getPrecisionFromBase} from '@alias_root/src/framework/ods/math';

//
//	Types
//

type ConvertFormatOptionsType = {
	decimalSeparator: string;
	thousandsSeparator: string;
};

//
//	Public
//

/**
 * Convert a string to a number
 *
 * @param value - The string to convert
 * @returns The number or null if the string could not be converted
 * @example
 *	stringToNumberWeak(0) -> 0
 *	stringToNumberWeak(NaN) -> null
 *	stringToNumberWeak('0') -> 0
 *	stringToNumberWeak('-1.1') -> -1.1
 *	stringToNumberWeak('') -> null
 *	stringToNumberWeak(' 1') -> null
 *	stringToNumberWeak('1 ') -> null
 */
export const stringToNumberWeak = (value: string | number): number | null => {
	// is the value already of type number?
	if (typeof value === 'number') {
		return !Number.isNaN(value) && Number.isFinite(value) ? value : null;
	}

	// Test for invalid characters
	// eslint-disable-next-line unicorn/better-regex
	if (!/^[+-]?(?:\d+(?:\.\d+)?|\.\d+)(?:E[+-]?\d+)?$/i.test(value)) {
		return null;
	}

	// Convert value to a number
	const num = Number(value);
	return Number.isNaN(num) ? null : num;
};

/**
 * Convert a string to a number
 *
 * @param value - The string to convert
 * @returns The number
 * @example
 *	stringToNumber(0) -> 0
 *	stringToNumber(NaN) -> throws TypeError
 *	stringToNumber('0') -> 0
 *	stringToNumber('-1.1') -> -1.1
 *	stringToNumber('') -> throws TypeError
 *	stringToNumber(' 1') -> throws TypeError
 *	stringToNumber('1 ') -> throws TypeError
 */
export const stringToNumber = (value: string | number): number => {
	const num = stringToNumberWeak(value);

	if (num === null) {
		throw new TypeError(`"${value}" is not a number`);
	}

	return num;
};

/**
 * Convert a string to a integer
 *
 * @param value - The string to convert
 * @returns The integer or null if the string could not be converted
 * @example
 *	stringToIntegerWeak(0) -> 0
 *	stringToIntegerWeak(NaN) -> null
 *	stringToIntegerWeak('0') -> 0
 *	stringToIntegerWeak('-1.1') -> null
 *	stringToIntegerWeak('') -> null
 *	stringToIntegerWeak(' 1') -> null
 *	stringToIntegerWeak('1 ') -> null
 */
export const stringToIntegerWeak = (value: string | number): number | null => {
	// is the value already a "real" integer, we just return the value
	if (typeof value === 'number' && Number.isInteger(value)) {
		return value;
	}

	// try to convert value to a number
	const num = stringToNumberWeak(value);
	if (num === null || !Number.isInteger(num)) {
		return null;
	}

	return num;
};

/**
 * Convert a string to a integer
 *
 * @param value - The string to convert
 * @returns The integer
 * @example
 *	stringToInteger(0) -> 0
 *	stringToInteger(NaN) -> throws TypeError
 *	stringToInteger('0') -> 0
 *	stringToInteger('-1.1') -> throws TypeError
 *	stringToInteger('') -> throws TypeError
 *	stringToInteger(' 1') -> throws TypeError
 *	stringToInteger('1 ') -> throws TypeError
 */
export const stringToInteger = (value: string | number): number => {
	const num = stringToIntegerWeak(value);

	if (num === null) {
		throw new TypeError(`"${value}" is not a number`);
	}

	return num;
};

/**
 *	Convert number into a formatted string.
 *
 * @param value - The number to convert.
 * @param roundingBase - The rounding base is the reciprocal of the smallest possible rounding step. The default of 100 round with precision of 2.
 * @param decimalSeparator - The decimal point.
 * @param thousandsSeparator - The thousands separator.
 * @returns The formatted string.
 * @example getFormattedStringFromNumberExt(12000.10) -> '12,000.10'
 */
export const getFormattedStringFromNumberExt = (
	value: number,
	roundingBase: number = 100,
	decimalSeparator: string = '.',
	thousandsSeparator: string = ',',
): string => {
	if (!isNumber(roundingBase) || roundingBase === 0) {
		throw new Error(`Invalid roundingBase: "${roundingBase}"`);
	}
	if (typeof decimalSeparator !== 'string' || decimalSeparator.length !== 1) {
		throw new Error(`Invalid decimalSeparator: "${decimalSeparator}"`);
	}
	if (typeof thousandsSeparator !== 'string' || thousandsSeparator.length !== 1) {
		throw new Error(`Invalid thousandsSeparator: "${thousandsSeparator}"`);
	}

	const roundedValue = roundByBase(value, roundingBase);
	const precision = getPrecisionFromBase(roundingBase);

	const parts = roundedValue.toFixed(precision).split('.');
	if (parts.length === 0 || parts.length > 2) {
		throw new Error(`Internal error. value="${value}"`);
	}

	let formatted = _getFormattedStringFromInteger(Number(parts[0]), thousandsSeparator);
	if (parts.length > 1) {
		formatted += decimalSeparator + parts[1];
	}

	return formatted;
};

/**
 *	Convert number into a formatted string (decimal point and thousands separator are externally configured)
 *
 * @param value - The number to convert.
 * @param roundingBase - The rounding base is the reciprocal of the smallest possible rounding step. The default of 100 round with precision of 2.
 * @returns The formatted string.
 * @example getFormattedStringFromNumber(12000.10) -> '12,000.10'
 */
export const getFormattedStringFromNumber = (value: number, roundingBase: number = 100): string =>
	getFormattedStringFromNumberExt(value, roundingBase, getDecimalSeparator(), getThousandsSeparator());

/**
 * convert a string (formatted with decimal point and thousands separator) into a number
 *
 * @param stringToConvert - the string to convert
 * @param [formatOptions] - an object containing decimalSeparator and thousandsSeparator
 * @returns The converted number or null if cannot be converted
 * @example	getNumberFromFormattedString('12.000,00') -> 12000
 */
export const getNumberFromFormattedString = (stringToConvert: string, formatOptions?: ConvertFormatOptionsType): number | null => {
	let decimalSeparator = getDecimalSeparator();
	let thousandsSeparator = getThousandsSeparator();
	let returnValue;

	if (formatOptions) {
		decimalSeparator = formatOptions.decimalSeparator ? formatOptions.decimalSeparator : getDecimalSeparator();
		thousandsSeparator = formatOptions.thousandsSeparator ? formatOptions.thousandsSeparator : getThousandsSeparator();
	}

	if (decimalSeparator !== '' || thousandsSeparator !== '') {
		let convertedDataValue = '';

		for (let i = 0; i < stringToConvert.length; i++) {
			if (thousandsSeparator === '' || stringToConvert.charAt(i) !== thousandsSeparator) {
				if (decimalSeparator !== '' && stringToConvert.charAt(i) === decimalSeparator) {
					convertedDataValue += '.';
				} else {
					convertedDataValue += stringToConvert.charAt(i);
				}
			}
		}

		returnValue = Number.parseFloat(convertedDataValue);
	} else {
		returnValue = Number.parseFloat(stringToConvert);
	}

	return Number.isNaN(returnValue) ? null : returnValue;
};

/**
 * return the global defined symbol for hours
 *
 * @returns the hour symbol
 * @example Convert.getHoursSymbol(); -> h
 */
export const getHoursSymbol = (): string => tr('h', 'h');

/**
 * return hours + defined hour symbol
 *
 * @param [hours] - hours value
 * @returns hours value with hours symbol attached
 * @example setHoursSymbol(8); -> '8h'
 */
export const setHoursSymbol = (hours?: string | number): string => {
	if (hours === undefined || hours === '') {
		return '';
	}

	return String(hours) + getHoursSymbol();
};

/**
 *	Convert a string of comma separated numbers to an array of numbers
 *
 * @param value - String of comma separated values.
 * @param separator - The separator.
 * @returns Array of number values.
 * @example stringToNumbers('1,2'); -> [1,2]
 */
export const stringToNumbers = (value: string | null, separator: string = ','): number[] => {
	if (typeof value !== 'string' && value !== null) {
		throw new TypeError('The value must be a string or null');
	}
	if (typeof separator !== 'string' || separator.length === 0) {
		throw new TypeError('The separator must be a non-empty string');
	}

	return value === null || value.length === 0
		? []
		: value
				.split(separator)
				.map((e) => e.trim())
				.filter((e) => e.length > 0)
				.map(stringToNumber);
};

/**
 *	Convert a string of comma separated strings to an array of strings
 *
 * @param value - String of comma separated values.
 * @param separator - The separator.
 * @returns Array of values.
 * @example stringToArray(''); -> []
 * @example stringToArray('1,,2'); -> [1,2]
 * @example stringToArray('false, null, 0,,'); -> ["false", "null", "0"]
 */
export const stringToArray = (value: string | null, separator: string = ','): string[] => {
	if (typeof value !== 'string' && value !== null) {
		throw new TypeError('The value must be a string or null');
	}

	if (value === null || value.length === 0) {
		return [];
	} else {
		return value
			.split(separator)
			.map((el) => el.trim())
			.filter((v) => v !== '') /* .filter(Boolean) */;
	}
};

/**
 * Handles various time input formats and converts them to a standardized decimal hour format.
 * @param timeString - current hours string
 * @returns number
 *
 * console.log(convertTimeStringToInteger("1.2")); // Output: 1.2
 * console.log(convertTimeStringToInteger(1h 30m)); // Output: 1.5
 * console.log(convertTimeStringToInteger("1:30")); // Output: 1.5
 */
export const convertTimeStringToInteger = (timeString: string): number => {
	// Remove all spaces from the input and replace ',' with '.'
	const cleanedTimeString = timeString.replaceAll(/\s+/g, '').replace(',', '.');
	const roundingBase = 100;

	if (cleanedTimeString === '' || cleanedTimeString === '0') {
		return 0;
	}

	let result: number;

	// Handle 'Xh Ym' format
	if (cleanedTimeString.includes('h') || cleanedTimeString.includes('m')) {
		const parts = cleanedTimeString.toLowerCase().match(/(\d+h)?(\d+m)?/);
		if (parts) {
			let hours = 0;
			let minutes = 0;

			if (parts[1]) {
				hours = Number.parseFloat(parts[1]);
			}
			if (parts[2]) {
				minutes = Number.parseFloat(parts[2]);
			}

			result = hours + minutes / 60;
		} else {
			throw new Error('Invalid time format');
		}
	}

	// Handle ':' format
	else if (cleanedTimeString.includes(':')) {
		const parts = cleanedTimeString.split(':');
		if (parts.length === 2) {
			const hours = Number.parseFloat(parts[0] || '0');
			const minutes = Number.parseFloat(parts[1].padStart(2, '0'));
			result = hours + minutes / 60;
		} else {
			throw new Error('Invalid time format');
		}
	}

	// Handle decimal format
	else if (cleanedTimeString.includes('.')) {
		result = Number.parseFloat(cleanedTimeString);
	}

	// Handle single number format (treat as hours)
	else if (!Number.isNaN(Number.parseFloat(cleanedTimeString))) {
		result = Number.parseFloat(cleanedTimeString);
	} else {
		throw new Error('Invalid time format');
	}

	// Round to two decimal places
	//return Math.round(result * 100) / 100;
	return roundByBase(result, roundingBase);
};

/*
 *	convert an integer number into a hour formatted string (formatted with user preference: tsHourFormat)
 */
export const getHourFormattedStringFromInteger = (value: number, format: HoursFormat = HoursFormat.DECIMAL): string => {
	switch (format) {
		case HoursFormat.TIME:
			return _convertFloatToHoursMinutes(value);
		case HoursFormat.DECLARATIVE:
			return _convertFloatToTimeString(value);
		case HoursFormat.DECIMAL:
		default:
			return getFormattedStringFromNumber(value);
	}
};

/*
 *	convert an integer number into a string (formatted with thousands separator)
 */
function _getFormattedStringFromInteger(integerToConvert: number, thousandsSeparator: string): string {
	const integerString = Math.abs(integerToConvert).toString();
	const integerStringLength = integerString.length;

	let formattedIntegerString = '';
	if (typeof thousandsSeparator === 'string' && thousandsSeparator !== '') {
		for (let i = 0; i < integerStringLength; i++) {
			if ((integerStringLength - i) % 3 === 0 && i > 0) {
				formattedIntegerString += thousandsSeparator + integerString.charAt(i);
			} else {
				formattedIntegerString += integerString.charAt(i);
			}
		}
	} else {
		formattedIntegerString = integerString;
	}

	// Object.is() is used because of eslint rule no-compare-neg-zero
	if (typeof integerToConvert === 'number' && (Math.sign(integerToConvert) === -1 || Object.is(Math.sign(integerToConvert), -0))) {
		return `-${formattedIntegerString}`;
	}

	return formattedIntegerString;
}

/**
 *
 * @param floatHours - current hours
 * @returns string
 *
 * console.log(convertFloatToTimeString(1.5));  // Output: "1:30"
 * console.log(convertFloatToTimeString(2.25)); // Output: "2:15"
 * console.log(convertFloatToTimeString(0.75)); // Output: "0:45"
 * console.log(convertFloatToTimeString(4));    // Output: "4:00"
 */
function _convertFloatToTimeString(floatHours: number): string {
	// Ensure the input is non-negative
	if (floatHours < 0) {
		throw new Error('Input must be a non-negative number');
	}

	// Extract hours and minutes
	const hours = Math.floor(floatHours);
	const minutes = Math.round((floatHours - hours) * 60);

	// Format hours and minutes
	const formattedHours = hours.toString();
	const formattedMinutes = minutes.toString().padStart(2, '0');

	// Combine hours and minutes
	return `${formattedHours}:${formattedMinutes}`;
}

/**
 *
 * @param floatHours - current hours
 * @returns string
 *
 * console.log(convertFloatToHoursMinutes(1.5));   // Output: "1h 30m"
 * console.log(convertFloatToHoursMinutes(2.25));  // Output: "2h 15m"
 * console.log(convertFloatToHoursMinutes(0.75));  // Output: "45m"
 * console.log(convertFloatToHoursMinutes(4));     // Output: "4h"
 */
function _convertFloatToHoursMinutes(floatHours: number): string {
	// Ensure the input is non-negative
	if (floatHours < 0) {
		throw new Error('Input must be a non-negative number');
	}

	// Extract hours and minutes
	const hours = Math.floor(floatHours);
	const minutes = Math.round((floatHours - hours) * 60);

	// Handle case where rounding minutes results in 60
	if (minutes === 60) {
		return `${hours + 1}h 0m`;
	}

	// Construct the output string
	let result = '';
	if (hours > 0) {
		result += `${hours}h `;
	}
	if (minutes > 0 || hours === 0) {
		result += `${minutes}m`;
	}

	return result.trim();
}
